import { syncDataLoaderFeature, selectionFeature } from "@headless-tree/core";
import { useState, useEffect, useMemo } from "react";
import { Button } from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";
import { useTree } from "@headless-tree/react";
import ChevronDownIcon from "@/icons/ChevronDownIcon";
import ChevronRightIcon from "@/icons/ChevronRightIcon";
import { ContentOutline } from "@/bundles/BundlePage";
import getExpandedTreePaths from "@/bundles/utils/getExpandedTreePaths";
import findCurrentContentIdTreeRoute from "@/bundles/utils/findCurrentContentIdTreeRoute";
import useCollapseTree from "./useCollapseTree";
import {
  chevron,
  container,
  header,
  treeContainer,
  treeItemCss,
  treeItemText,
  lineItemIndicatorCss,
} from "./GeneaologyPanel.styles";
import type { TreeItem } from "../../types";

const ButtonWithTrackEvent = withTrackEvent(Button);

type GeneaologyPanelProps = {
  contentOutline: ContentOutline;
  updateContentId: (id: string) => void;
  currentContentIdOrTree: string;
};

export default function GeneaologyPanel({
  contentOutline,
  updateContentId,
  currentContentIdOrTree,
}: GeneaologyPanelProps) {
  const treeItems = contentOutline?.items ?? {};
  const currentContentIdTree = useMemo(
    () =>
      currentContentIdOrTree.includes("/")
        ? currentContentIdOrTree
        : findCurrentContentIdTreeRoute(treeItems, currentContentIdOrTree),
    [treeItems, currentContentIdOrTree],
  );
  const [focusedItemId, setFocusedItemId] = useState<string | null>(
    currentContentIdTree ?? contentOutline.initial_focused_item_id ?? null,
  );
  const [expandedItems, setExpandedItems] = useState<string[]>(
    getExpandedTreePaths(currentContentIdTree),
  );

  useCollapseTree({
    expandedItems,
    setExpandedItems,
    treeItems,
  });

  useEffect(() => {
    if (!focusedItemId) return;
    const focusedItem = treeItems[focusedItemId];
    if (focusedItem) {
      updateContentId(focusedItemId);
    }
  }, [focusedItemId, treeItems, updateContentId]);

  const tree = useTree<TreeItem>({
    state: {
      focusedItem: focusedItemId,
      expandedItems,
    },
    setExpandedItems,
    setFocusedItem: setFocusedItemId,
    rootItemId: contentOutline?.root_item_id,
    getItemName: (item) => item.getItemData().name,
    isItemFolder: (item) => Boolean(item.getItemData().isFolder),
    dataLoader: {
      getItem: (itemId) => {
        const item = treeItems[itemId];
        if (!item) throw new Error(`Tree item not found: ${itemId}`);
        return item;
      },
      getChildren: (itemId) => treeItems?.[itemId]?.children || [],
    },
    indent: 20,
    features: [selectionFeature, syncDataLoaderFeature],
  });

  useEffect(() => {
    tree.rebuildTree();
  }, [tree, treeItems]);

  if (
    !contentOutline ||
    !contentOutline.root_item_id ||
    !treeItems[contentOutline.root_item_id]
  ) {
    return null;
  }

  const rootItem = treeItems[contentOutline.root_item_id];
  if (!rootItem) return null;

  return (
    <div css={container}>
      <div
        css={header}
        onClick={() => {
          tree.setState((s) => ({
            ...s,
            focusedItem: null,
          }));
          setExpandedItems([]);
          setFocusedItemId(contentOutline.root_item_id);
        }}
      >
        {rootItem.name}
      </div>
      <div {...tree.getContainerProps()} css={treeContainer}>
        {tree.getItems().map((item) => (
          <ButtonWithTrackEvent
            key={item.getId()}
            __cssFor={{ root: treeItemCss(item.isFocused()) }}
            {...item.getProps()}
            role="treeitem"
            aria-selected={item.isSelected()}
            actionName={actionNames.interact}
            objectType={objectTypes.content}
            streamProperties={{
              description: "Select item from outline tree on bundles page",
            }}
          >
            <div
              css={treeItemText}
              style={{
                marginLeft: `${item.getItemMeta().level * 15}px`,
              }}
            >
              <span
                style={{
                  marginRight: item.getChildren().length === 0 ? "28px" : "0px",
                }}
              >
                <div css={lineItemIndicatorCss(item.isFocused())} />
                {item.isExpanded() && item.getChildren().length > 0 ? (
                  // @ts-ignore
                  <ChevronDownIcon css={chevron} />
                ) : null}
                {!item.isExpanded() && item.getChildren().length > 0 ? (
                  // @ts-ignore
                  <ChevronRightIcon css={chevron} />
                ) : null}
              </span>
              <span>{item.getItemName()}</span>
            </div>
          </ButtonWithTrackEvent>
        ))}
      </div>
    </div>
  );
}
